import { ReactNode } from "react";
import styled from "styled-components";
import { designSystemVars as vars } from "../tokens";
import { Surface } from "./Surface";

export interface WarningBannerProps {
  title: ReactNode;
  icon?: ReactNode;
  children: ReactNode;
  action?: ReactNode;
  className?: string;
  layoutGap?: keyof typeof vars.spacing;
}

const WarningSurface = styled(Surface).attrs({
  role: "status",
  padding: "none",
  radius: "sm",
})<{ $layoutGap: keyof typeof vars.spacing }>`
  display: flex;
  width: 100%;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  gap: ${vars.spacing.sm};
  padding: ${vars.spacing.md};
  border: ${vars.borderWidth.medium} solid ${vars.color.feedback.warning.border};
  background: ${vars.color.background.transparent};
  font-family: ${vars.typography.family.sans};
  margin-block: ${({
    $layoutGap,
  }: {
    $layoutGap: keyof typeof vars.spacing;
  }) =>
    $layoutGap === "none"
      ? vars.spacing.xl
      : `calc(${vars.spacing.xl} - ${vars.spacing[$layoutGap]})`};

  &:first-child,
  [data-warning-banner] + & {
    margin-top: 0;
  }
`;

const WarningTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${vars.spacing.sm};
  color: ${vars.color.feedback.warning.text};
  font-size: ${vars.typography.size.sm};
  font-weight: ${vars.typography.weight.semibold};
  line-height: ${vars.typography.lineHeight.xs};
`;

const WarningMessage = styled.div`
  color: ${vars.color.text.primary};
  font-size: ${vars.typography.size.xs};
  font-weight: ${vars.typography.weight.regular};
  line-height: ${vars.typography.lineHeight.sm};
  text-align: center;
`;

export const WarningBanner = ({
  title,
  icon,
  children,
  action,
  className,
  layoutGap = "none",
}: WarningBannerProps) => (
  <WarningSurface
    className={className}
    $layoutGap={layoutGap}
    data-warning-banner=""
  >
    <WarningTitle>
      {icon}
      {title}
    </WarningTitle>
    <WarningMessage>{children}</WarningMessage>
    {action}
  </WarningSurface>
);
